import { createAsyncThunk } from "@reduxjs/toolkit";
import onboardingApi from "../api/onboardingApi";
import {
  OnboardingStatusResponse,
  GenerateLandingPagePayload,
} from "../types/onboardingTypes";
import { AxiosError, isAxiosError } from "axios";
import { displayAlert } from "../slice/alertSlice";
import { CreatorBrief } from "../types/creatorBriefsTypes";
import { PersonaAPIResponseData } from "../types/personasTypes";
import { RootState } from "../root";

export const checkOnboardingStatusThunk = createAsyncThunk<
  OnboardingStatusResponse,
  string,
  { state: RootState; rejectValue: string }
>("onboarding/checkStatus", async (taskId, { rejectWithValue }) => {
  try {
    const response = await onboardingApi.fetchOnboardingStatus(taskId);
    return response.data;
  } catch (e: unknown) {
    const error = e as AxiosError<{ detail?: string }>;
    console.error("Checking onboarding status failed:", error);
    return rejectWithValue(
      error.response?.data?.detail || "Failed to check onboarding status.",
    );
  }
});

export interface GrowthMarketingPayload {
  biggest_growth_challenge: string;
  monthly_marketing_budget: number;
  worked_with_creators: string;
}

export const postGrowthMarketingSetupThunk = createAsyncThunk<
  object,
  GrowthMarketingPayload,
  { rejectValue: string }
>("onboarding/postGrowthMarketing", async (payload, { rejectWithValue }) => {
  try {
    const response = await onboardingApi.postGrowthMarketingSetup(payload);
    return response.data;
  } catch (e: unknown) {
    const error = e as AxiosError<{ detail?: string }>;
    console.error("Growth marketing setup API call failed:", error);
    return rejectWithValue(
      error.response?.data?.detail || "Failed to save marketing setup.",
    );
  }
});

export interface GenerateLandingPageThunkArg {
  brief: CreatorBrief;
  persona: PersonaAPIResponseData;
  companyId: number;
  creator_id: number;
  brandId: number;
  writingStyle: string;
}

interface GenerateLandingPageResponse {
  briefId: number;
  preview_url: string;
}

interface GenerateLandingPageRejectValue {
  briefId: number;
  error: string;
}

export const generateLandingPageThunk = createAsyncThunk<
  GenerateLandingPageResponse,
  GenerateLandingPageThunkArg,
  { rejectValue: GenerateLandingPageRejectValue }
>(
  "onboarding/generateLandingPage",
  async (thunkArg, { dispatch, rejectWithValue }) => {
    const { brief, persona, companyId, brandId, writingStyle, creator_id } =
      thunkArg;

    try {
      const apiPayload: GenerateLandingPagePayload = {
        company_id: companyId,
        brand_id: brandId,
        campaign_id: brief.campaign.id,
        persona_id: persona.id,
        creator_id: creator_id,
        brief_id: brief.id,
        brief_data: {
          persona: persona,
        },
        recipe: "default_cpg",
        writing_style: writingStyle,
      };

      const response = await onboardingApi.generateLandingPage(apiPayload);

      return {
        briefId: brief.id,
        preview_url: response.data.funnel.preview_url,
      };
    } catch (error: unknown) {
      console.error("Landing page generation failed:", error);
      if (isAxiosError(error) && error.response?.status === 429) {
        return rejectWithValue({
          briefId: brief.id,
          error: "Rate limited.",
        });
      }
      let errorMessage = "Landing page generation failed. Please try again.";

      if (error instanceof AxiosError && error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      dispatch(
        displayAlert({
          type: "error",
          text: errorMessage,
        }),
      );

      return rejectWithValue({
        briefId: brief.id,
        error: errorMessage,
      });
    }
  },
);

export const fetchStubCampaignThunk = createAsyncThunk(
  "onboarding/fetchStubCampaign",
  async (brandId: number, { rejectWithValue }) => {
    try {
      const response = await onboardingApi.fetchStubCampaign(brandId);
      return response.data;
    } catch (e: unknown) {
      const error = e as AxiosError<{ detail?: string }>;
      return rejectWithValue(
        error.response?.data?.detail ||
          "Failed to initialize onboarding campaign.",
      );
    }
  },
);
